import React, { Component } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatTimeOfDay } from '@/utils/time-format';

/**
 * Error boundary specifically designed for the AnalysisDashboard component
 * Provides graceful error handling without breaking the rest of the analysis page
 */
class DashboardErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('Dashboard Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Report error to monitoring service if available
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: `Dashboard Error: ${error.message}`,
        fatal: false,
      });
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
    }));
  };

  render() {
    if (this.state.hasError) {
      const { retryCount } = this.state;
      const maxRetries = 3;
      const canRetry = retryCount < maxRetries;

      return (
        <Card className="p-4 xs:p-6 sm:p-8 border border-red-200 shadow-lg bg-red-50">
          <div className="text-center">
            <div className="mb-4">
              <h2 className="text-lg xs:text-xl font-bold text-red-800 mb-2">
                Performance Dashboard Error
              </h2>
              <p className="text-red-700 font-medium mb-2 text-sm xs:text-base">
                The dashboard encountered an unexpected error
              </p>
              <p className="text-red-600 text-xs xs:text-sm mb-3 leading-relaxed">
                Don't worry - the rest of the analysis page is still working normally.
              </p>
            </div>

            {canRetry && (
              <div className="mb-4">
                <Button
                  onClick={this.handleRetry}
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-100 hover:border-red-400"
                >
                  Try Again {retryCount > 0 && `(${retryCount}/${maxRetries})`}
                </Button>
              </div>
            )}

            {!canRetry && (
              <div className="mb-4">
                <p className="text-red-600 text-xs xs:text-sm mb-3">
                  Maximum retry attempts reached. Please refresh the page or contact support.
                </p>
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-100 hover:border-red-400"
                >
                  Refresh Page
                </Button>
              </div>
            )}

            {/* Technical details for debugging */}
            <details className="text-left bg-red-100 rounded-lg p-3 mb-4">
              <summary className="text-xs xs:text-sm font-medium text-red-800 cursor-pointer hover:text-red-900 transition-colors">
                Technical Details
              </summary>
              <div className="mt-2 text-xxs xs:text-xs text-red-600 space-y-1">
                <div className="break-words">
                  <span className="font-medium">Error:</span> {this.state.error?.message || 'Unknown error'}
                </div>
                {this.state.error?.stack && (
                  <div className="break-words">
                    <span className="font-medium">Stack:</span>
                    <pre className="mt-1 text-xxs whitespace-pre-wrap font-mono">
                      {this.state.error.stack.split('\n').slice(0, 3).join('\n')}
                    </pre>
                  </div>
                )}
                <div className="break-words">
                  <span className="font-medium">Retry Count:</span> {retryCount}
                </div>
              </div>
            </details>

            <div className="text-xxs xs:text-xs text-red-400">
              Error occurred at: {formatTimeOfDay(new Date())}
            </div>
          </div>
        </Card>
      );
    }

    return this.props.children;
  }
}

export default DashboardErrorBoundary;